import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import UserForm from "./UserForm";
import "./UserManagement.css";

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (user?.role === "admin") {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/users");
      setUsers(response.data.data.users);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch users");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setShowModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      setSuccess("User deleted successfully");
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete user");
      setTimeout(() => setError(""), 5000);
    }
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingUser(null);
    fetchUsers();
    setSuccess(editingUser ? "User updated successfully" : "User created successfully");
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleFormCancel = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter((u) => {
    const matchesSearch = u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === "all" || u.role === filterRole;
    const matchesPlan = filterPlan === "all" || u.subscription?.plan === filterPlan;
    return matchesSearch && matchesRole && matchesPlan;
  });

  if (user?.role !== "admin") {
    return (
      <div className="admin-um-container">
        <div className="container mt-5">
          <div className="alert alert-danger">
            <i className="fa fa-exclamation-triangle me-2"></i>
            Access Denied. Admin privileges required.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-um-container">
      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col-12">
            <div className="admin-um-header-card">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="admin-um-title mb-2">
                    <i className="fa fa-users me-2"></i>
                    User Management
                  </h2>
                  <p className="admin-um-subtitle mb-0">
                    Manage all registered users and their permissions
                  </p>
                </div>
                <button
                  className="admin-um-btn-primary"
                  onClick={handleAddUser}
                >
                  <i className="fa fa-user-plus me-2"></i>
                  Add New User
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="fa fa-check-circle me-2"></i>
            {success}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccess("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        <div className="row mb-4">
          <div className="col-12">
            <div className="admin-um-filter-card">
              <div className="row g-3">
                <div className="col-md-5">
                  <div className="admin-um-search-wrapper">
                    <i className="fa fa-search admin-um-search-icon"></i>
                    <input
                      type="text"
                      className="admin-um-search-input"
                      placeholder="Search by email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="admin-um-select"
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                  >
                    <option value="all">All Roles</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="admin-um-select"
                    value={filterPlan}
                    onChange={(e) => setFilterPlan(e.target.value)}
                  >
                    <option value="all">All Plans</option>
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div className="col-md-1">
                  <button
                    className="admin-um-btn-outline w-100"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterRole("all");
                      setFilterPlan("all");
                    }}
                    title="Clear filters"
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="admin-um-table-card">
              <div className="admin-um-stats-row">
                <div className="admin-um-stat-item">
                  <i className="fa fa-users admin-um-stat-icon"></i>
                  <div>
                    <div className="admin-um-stat-value">{filteredUsers.length}</div>
                    <div className="admin-um-stat-label">Total Users</div>
                  </div>
                </div>
                <div className="admin-um-stat-item">
                  <i className="fa fa-shield admin-um-stat-icon"></i>
                  <div>
                    <div className="admin-um-stat-value">
                      {filteredUsers.filter((u) => u.role === "admin").length}
                    </div>
                    <div className="admin-um-stat-label">Admins</div>
                  </div>
                </div>
                <div className="admin-um-stat-item">
                  <i className="fa fa-star admin-um-stat-icon"></i>
                  <div>
                    <div className="admin-um-stat-value">
                      {filteredUsers.filter((u) => u.subscription?.plan !== "free").length}
                    </div>
                    <div className="admin-um-stat-label">Premium Users</div>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading users...</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fa fa-inbox display-1 text-muted"></i>
                  <p className="mt-3 text-muted">No users found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="admin-um-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Plan</th>
                        <th>Credits</th>
                        <th>Free Trial</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u._id}>
                          <td>
                            <div className="admin-um-user-email">
                              <i className="fa fa-envelope me-2"></i>
                              {u.email}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`admin-um-badge ${
                                u.role === "admin"
                                  ? "admin-um-badge-danger"
                                  : "admin-um-badge-info"
                              }`}
                            >
                              {u.role === "admin" ? (
                                <i className="fa fa-shield me-1"></i>
                              ) : (
                                <i className="fa fa-user me-1"></i>
                              )}
                              {u.role}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`admin-um-badge ${
                                u.subscription?.plan === "premium"
                                  ? "admin-um-badge-warning"
                                  : u.subscription?.plan === "basic"
                                  ? "admin-um-badge-primary"
                                  : "admin-um-badge-secondary"
                              }`}
                            >
                              {u.subscription?.plan === "premium" && (
                                <i className="fa fa-star me-1"></i>
                              )}
                              {u.subscription?.plan || "free"}
                            </span>
                          </td>
                          <td>
                            <span className="admin-um-credits">
                              {u.subscription?.extractionCredits || 0}
                            </span>
                          </td>
                          <td>
                            {u.freeTrialUsed ? (
                              <span className="admin-um-badge admin-um-badge-secondary">
                                <i className="fa fa-check-circle me-1"></i>
                                Used
                              </span>
                            ) : (
                              <span className="admin-um-badge admin-um-badge-success">
                                <i className="fa fa-hourglass-half me-1"></i>
                                Available
                              </span>
                            )}
                          </td>
                          <td className="admin-um-date">
                            {new Date(u.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <div className="admin-um-actions">
                              <button
                                className="admin-um-action-btn admin-um-action-edit"
                                onClick={() => handleEditUser(u)}
                                title="Edit user"
                              >
                                <i className="fa fa-pencil"></i>
                              </button>
                              <button
                                className="admin-um-action-btn admin-um-action-delete"
                                onClick={() => handleDeleteUser(u._id)}
                                title="Delete user"
                                disabled={u._id === user._id}
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <UserForm
          user={editingUser}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default UserManagement;
